import { yupResolver } from '@hookform/resolvers/yup';
import {
  getV1CompaniesCompanyIdProjectsProjectIdAutoAddOnAssignments,
  type ModelError_Item,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type DefaultValues,
  FieldErrors,
  FormProvider,
  type Path,
  type Resolver,
  useForm,
  useWatch,
} from 'react-hook-form';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import type { FileAttachmentsHandler } from '~formsShared/components/FileAttachments';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import type { CompanyBasic } from '~hooks/useCompany/models';
import type { CompanySettings } from '~hooks/useCompanySettings/models';
import useRecordCompanySettings from '~hooks/useCompanySettings/useRecordCompanySettings';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import type { MaterialRate } from '~hooks/useMaterialRates/models';
import type { Project } from '~hooks/useProjects/models';
import type { ServiceClass } from '~hooks/useServiceClass/models';
import useServiceClassOptions from '~hooks/useServiceClass/useServiceClassOptions';
import { isFreightRateRequiredForDepartment } from '~pages/Sales/sharedSales/rateRequirements';
import { queryClient } from '~queries/queryClient';
import { accountQueryOptions } from '~queries/useAccountQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import type { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import type { FreightRatesHandler } from '../components/FreightRates';
import { mapInitialMaterialRatesToFormRows } from '../materialRatesFormUtils';
import {
  EmptyProjectMaterialTotalItem,
  makeProjectFormSchema,
  projectFormSchema,
  type ProjectFormSchemaInterface,
  setDefaultProjectValues,
} from '../projectFormSchema';
import { type FieldMetadata, projectFieldMetadata } from './fieldMetadata';
import {
  type FetchRatesCallback,
  type ProjectFormV2Preloads,
  useProjectFormV2Preloads,
} from './useProjectFormV2Preloads';

type ProjectFormV2Flags = {
  tomlinsonOrderModal: boolean;
  showHaulerRateTypeCard: boolean;
  driverAgent: boolean;
  projectMaterialRateSeeding: boolean;
  liveEtaEnabled: boolean;
  fastWeighEnabled: boolean;
};

type ProjectFieldRuntimeMetadata<K extends keyof ProjectFormSchemaInterface> =
  FieldMetadata & {
    currentValue: ProjectFormSchemaInterface[K];
    isDirty: boolean;
    isAIEdited: boolean;
    validationError: string | undefined;
  };

/**
 * Callbacks forwarded to `<FreightRates/>` via the FreightRatesPanel. The
 * shape covers every callback prop on `FreightRatesProps` that the legacy
 * `<ProjectForm/>` actually wires; optional props the legacy form never
 * passes are deliberately omitted so the panel can spread the map without
 * fighting TypeScript's `exactOptionalPropertyTypes` settings. A compile-time
 * `expectTypeOf` gate enforces that every prop the panel passes is a
 * non-`any` defined value.
 */
type ProjectFormV2Callbacks = {
  fetchCustomerRates: FetchRatesCallback;
  fetchVendorRates: FetchRatesCallback;
  fetchDriverRates: FetchRatesCallback;
  fetchParentAddOnAssignments: (
    rateOwnerType: RateOwnerType,
  ) => (signal?: AbortSignal) => Promise<AddOnAssignment[]>;
  onDirtyStatusChange: () => void;
};

type ProjectFormV2ContextValue = {
  dispatchNumberLabel: (fallbackKey: string) => string;
  flags: ProjectFormV2Flags;
  // Company settings, NOT Split flags — `flags` keys mirror FeatureFlags enum
  // keys verbatim, so settings live as their own fields.
  showDefaultDigitalTicketView: boolean;
  showContractNumber: boolean;
  showServiceClass: boolean;
  // Read for the project's company, which can differ from the user's own.
  isDepartmentRequired: boolean;
  isSalespersonRequired: boolean;
  isFreightRateRequired: boolean;
  keepFreightRatesMounted: boolean;
  isRateByDepartmentNameRuleOn: boolean;
  isTaxRequired: boolean;
  serviceClassOptions: ServiceClass[];
  defaultProject: Project;
  // Copy-and-edit signal + the source project id. The FreightRatesPanel
  // reads both: `isCopyAndEdit` carries the source rate details over (and
  // tells FreightRates to strip SRS ids so they recreate as new), and
  // `sourceProjectId` is the id FreightRates fetches the source SRS from
  // (defaultProject.id is stripped on copy). Mirrors legacy parity.
  isCopyAndEdit: boolean;
  sourceProjectId: string | undefined;
  watchedSelectedCompany: ProjectFormSchemaInterface['company'] | undefined;
  watchedPhases: ProjectFormSchemaInterface['phases'] | undefined;
  watchedAccount: ProjectFormSchemaInterface['account'] | undefined;
  watchedPrevailingWage: ProjectFormSchemaInterface['prevailingWage'] | undefined;
  watchedTaxRateSchedule: ProjectFormSchemaInterface['taxRateSchedule'] | undefined;
  preloads: ProjectFormV2Preloads;
  callbacks: ProjectFormV2Callbacks;
  applyAIPatch: (partial: Partial<ProjectFormSchemaInterface>) => void;
  aiEditedFields: ReadonlySet<keyof ProjectFormSchemaInterface>;
  getFieldMetadata: <K extends keyof ProjectFormSchemaInterface>(
    key: K,
  ) => ProjectFieldRuntimeMetadata<K>;
  validateField: <K extends keyof ProjectFormSchemaInterface>(key: K) => Promise<boolean>;
  // The FreightRatesPanel writes this ref; the shell's submit calls
  // freightRatesRef.current.onSubmit(cb) at submit time to extract the
  // rule-based UI's site-rate-schedule operations (+ optional add-on
  // assignments) and forward them to the dialog's submit pipeline. Lives
  // on context because the shell and the panel can't share a ref without
  // it. The retired Customer/Vendor/Driver triple UI is not supported in v2 —
  // only the rule-based UI.
  freightRatesRef: React.MutableRefObject<FreightRatesHandler | null>;
  onRateCardsLoadedChange?: (areRateCardsLoaded: boolean) => void;
  // The FileAttachmentsPanel writes this ref; the shell's
  // fileAttachmentsOnSubmit/onClose handles call
  // fileAttachmentsRef.current.onSubmit(projectId) / onCancel() at submit/close
  // time so the queued attachments are actually uploaded. Lives on context
  // because the shell and the panel can't share a ref otherwise — same pattern
  // as freightRatesRef. Mirrors the legacy form.
  fileAttachmentsRef: React.MutableRefObject<FileAttachmentsHandler | null>;
};

/**
 * Dev/test-only window surface. Installed by the Provider when
 * `import.meta.env.MODE !== 'production'`, so the deferred AI-patch e2e suite
 * and manual QA flows can drive the form's AI surface without rendering a
 * helper UI. Production builds DO NOT install this global — `window
 * .__projectFormV2__` is `undefined` in prod.
 */
type ProjectFormV2WindowHook = {
  applyAIPatch: (partial: Partial<ProjectFormSchemaInterface>) => void;
  getAIEditedFields: () => Array<keyof ProjectFormSchemaInterface>;
};

declare global {
  interface Window {
    __projectFormV2__?: ProjectFormV2WindowHook;
  }
}

const ProjectFormV2Context = createContext<ProjectFormV2ContextValue | null>(null);

export function useProjectFormV2Context(): ProjectFormV2ContextValue {
  const ctx = useContext(ProjectFormV2Context);
  if (!ctx) {
    throw new Error('useProjectFormV2Context must be used inside ProjectFormV2Provider');
  }
  return ctx;
}

type ProviderProps = {
  defaultProject: Project;
  defaultCompany?: Nullable<CompanyBasic>;
  // Async errors raised by the parent save (422). The Provider maps each
  // error.field to a schema key and surfaces it via RHF setError so the
  // matching panel renders the message inline. Unmapped fields fall back to
  // a toast so the user still sees feedback for cross-cutting BE errors
  // (e.g., model: 'project', field: 'account' on a join-table violation).
  errors?: ModelError_Item[];
  // Material rates fetched by the dialog (separate API from the project
  // body). The Provider maps them into form rows via the legacy helper and
  // setValue('materialRates') once they arrive, mirroring the legacy form.
  // `null` means "not yet loaded" so the effect
  // stays inert until the dialog flips it to an array (including `[]`).
  initialMaterialRates?: MaterialRate[] | null;
  isCopyAndEdit?: boolean;
  // On copy-and-edit `defaultProject` has its id stripped (so save creates a
  // new project), but the FreightRates widget still needs the ORIGINAL
  // project id to fetch the source site-rate-schedules and recreate them.
  // Mirrors legacy `projectIdForDataFetching = sourceProjectId ?? id`.
  sourceProjectId?: string;
  // The quote preview CTA lives in the dialog footer, outside the form
  // provider, so it cannot watch the account field itself.
  onCustomerAccountChange?: (hasCustomerAccount: boolean) => void;
  // Same reason as above: the CTA sits outside the form provider, so it cannot
  // observe the rate cards settling on its own.
  onRateCardsLoadedChange?: (areRateCardsLoaded: boolean) => void;
  children: ReactNode;
};

// The defaults below are a mount-only snapshot, so the form waits for the project
// company's settings before it mounts.
export function ProjectFormV2Provider(props: ProviderProps) {
  const { userStore } = useStores();
  const [recordCompanyId, setRecordCompanyId] = useState<string | undefined>(
    () => props.defaultProject?.company?.id || props.defaultCompany?.id || undefined,
  );
  const recordCompanySettings = useRecordCompanySettings(recordCompanyId);

  if (!recordCompanySettings) {
    return <LoadingSpinner isVisible sx={{ height: '100%' }} />;
  }

  return (
    <ProjectFormV2FormProvider
      {...props}
      recordCompanySettings={recordCompanySettings}
      isUserInRecordCompany={
        !recordCompanyId || recordCompanyId === userStore.userCompany?.id
      }
      onRecordCompanyIdChange={setRecordCompanyId}
    />
  );
}

function ProjectFormV2FormProvider({
  defaultProject,
  defaultCompany,
  errors,
  initialMaterialRates,
  isCopyAndEdit,
  sourceProjectId,
  onCustomerAccountChange,
  onRateCardsLoadedChange,
  recordCompanySettings,
  isUserInRecordCompany,
  onRecordCompanyIdChange,
  children,
}: ProviderProps & {
  recordCompanySettings: CompanySettings;
  isUserInRecordCompany: boolean;
  onRecordCompanyIdChange: (companyId: string | undefined) => void;
}) {
  const tomlinsonOrderModal = useFeatureFlag({
    featureFlagKey: FeatureFlags.tomlinsonOrderModal,
  });
  // Generic rate-card flag that swaps freight-rates → hauler-rate-card,
  // independently of the tomlinsonOrderModal bundle. The FreightRatesPanel
  // shows the compact rate-details card when either flag is on; validation
  // stays on tomlinsonOrderModal only.
  const showHaulerRateTypeCard = useFeatureFlag({
    featureFlagKey: FeatureFlags.showHaulerRateTypeCard,
  });
  const driverAgent = useFeatureFlag({ featureFlagKey: FeatureFlags.driverAgent });
  const selectiveRateApplicationDefault = useFeatureFlag({
    featureFlagKey: FeatureFlags.selectiveRateApplicationDefault,
  });
  const projectMaterialRateSeeding = useFeatureFlag({
    featureFlagKey: FeatureFlags.projectMaterialRateSeeding,
  });
  const liveEtaEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.liveEtaEnabled,
  });
  const fastWeighEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.fastWeighEnabled,
  });

  const { singular: dispatchNumberLabel } = useDispatchNumberLabel();

  const { toasterStore, userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);

  const chaseMissingTicketsDefaultOn = userStore.companySettings.isEnabled(
    'chase_missing_tickets_default_on',
  );
  const liveEtaDefault =
    liveEtaEnabled && userStore.companySettings.isEnabled('live_eta_default');
  const showDefaultDigitalTicketView = userStore.companySettings.isEnabled(
    'show_default_digital_ticket_view',
  );
  const showContractNumber = userStore.companySettings.isEnabled('show_contract_number');
  const showServiceClass = userStore.companySettings.isEnabled('show_service_class');
  const isDepartmentRequired = recordCompanySettings.isEnabled('require_department');
  const isSalespersonRequired = recordCompanySettings.isEnabled('require_salesperson');
  const canPrefillSalesperson =
    isSalespersonRequired &&
    isUserInRecordCompany &&
    Boolean(userStore.user?.isSalesperson);
  const isRateByDepartmentNameRuleOn = recordCompanySettings.isEnabled(
    'require_rates_by_department_name',
  );
  const { canCreateMaterialRate, canEditMaterialRate } = userStore.getPermissions();
  const canManageMaterialRates = canCreateMaterialRate && canEditMaterialRate;
  const isTaxRequired = recordCompanySettings.isEnabled('require_tax');

  const flags = useMemo<ProjectFormV2Flags>(
    () => ({
      tomlinsonOrderModal,
      showHaulerRateTypeCard,
      driverAgent,
      projectMaterialRateSeeding,
      liveEtaEnabled,
      fastWeighEnabled,
    }),
    [
      tomlinsonOrderModal,
      showHaulerRateTypeCard,
      driverAgent,
      projectMaterialRateSeeding,
      liveEtaEnabled,
      fastWeighEnabled,
    ],
  );

  // setDefaultProjectValues returns the ~53 schema-side fields. The 9
  // freight-rate side-channel fields (customerRate*/vendorRate*/driverRate*)
  // are written into form state by the FreightRates widget at runtime; RHF's
  // DefaultValues type is DeepPartial<TFieldValues>, so leaving them out of
  // the initial defaultValues is structurally fine.
  // Editing keeps the project's own requester; creating (incl. copy-and-edit,
  // where ids are stripped) pre-fills it with the current user. Mirrors the
  // dialog's `isEditing = Boolean(project) && !isCopyAndEdit`.
  const isEditing = Boolean(defaultProject?.id) && !isCopyAndEdit;

  const defaultValues = useMemo<DefaultValues<ProjectFormSchemaInterface>>(() => {
    // On create, seed `projectTerms` with the company's default Quote PDF terms
    // so the user sees (and can override) what will ship on the quote. Mirrors
    // the legacy form — passing `undefined` here was the TRE-15970
    // regression that left the field blank on new projects.
    const base = setDefaultProjectValues(
      defaultProject,
      defaultCompany,
      chaseMissingTicketsDefaultOn,
      userStore.userCompany?.defaultProjectTerms ?? null,
      selectiveRateApplicationDefault,
      liveEtaDefault,
      recordCompanySettings,
    );
    const shouldPrefillSalesperson = !defaultProject && canPrefillSalesperson;

    // Pre-fill `requester` with the current user on create — mirrors the
    // legacy form (`setValue('requester', userStore.user)` when
    // not editing). Done here (a Provider default) instead of a panel effect
    // so the form initializes fully populated and panels stay render-only.
    if (!isEditing && userStore?.user) {
      return {
        ...base,
        requester: userStore.user,
        ...(shouldPrefillSalesperson ? { salespeople: [userStore.user] } : {}),
      } as DefaultValues<ProjectFormSchemaInterface>;
    }

    return base as DefaultValues<ProjectFormSchemaInterface>;
  }, [
    defaultProject,
    defaultCompany,
    chaseMissingTicketsDefaultOn,
    selectiveRateApplicationDefault,
    liveEtaDefault,
    isEditing,
    recordCompanySettings,
    canPrefillSalesperson,
    userStore?.user,
    userStore?.userCompany?.defaultProjectTerms,
  ]);

  // yupResolver is typed from yup.InferType<typeof projectFormSchema>; our
  // form-state type is the interface with the 9 freight-rate side-channels.
  // The resolver only validates the schema fields and ignores the extras at
  // runtime, so the Resolver<ProjectFormSchemaInterface> shape is the right
  // boundary cast (no `any`/`unknown` involved).
  const resolver = useMemo<Resolver<ProjectFormSchemaInterface>>(
    () =>
      yupResolver(
        makeProjectFormSchema(
          fastWeighEnabled,
          recordCompanySettings,
          canManageMaterialRates,
        ),
      ) as Resolver<ProjectFormSchemaInterface>,
    [fastWeighEnabled, recordCompanySettings, canManageMaterialRates],
  );

  const methods = useForm<ProjectFormSchemaInterface>({
    resolver,
    mode: 'onChange',
    defaultValues,
  });

  // Subscribe to `company` so panels can read `watchedSelectedCompany` from
  // context without each one taking a separate useWatch (and tripping the
  // panel-grep guard that forbids useWatch / watch inside panels). The same
  // subscription drives the department refresh below and is reused by the
  // cascading reset (company → account null) and the RequesterPanel
  // (companyId on the getCompanyUsersTypeahead query).
  const watchedSelectedCompany = useWatch({
    control: methods.control,
    name: 'company',
  });

  // Subscribe to `phases` so the MaterialsPanel re-renders with the
  // active phase list whenever the PhaseDrawer mutates it. Same rationale
  // as watchedSelectedCompany — keeps `useWatch` out of every consuming panel.
  const watchedPhases = useWatch({
    control: methods.control,
    name: 'phases',
  });

  const watchedDepartment = useWatch({ control: methods.control, name: 'department' });
  const isFreightRateRequired = isFreightRateRequiredForDepartment(
    recordCompanySettings,
    watchedDepartment,
  );
  const [freightRatesWereRequired, setFreightRatesWereRequired] = useState(false);
  if (isFreightRateRequired && !freightRatesWereRequired) {
    setFreightRatesWereRequired(true);
  }
  // Unmounting FreightRates would drop its unsaved card edits.
  const keepFreightRatesMounted = isFreightRateRequired || freightRatesWereRequired;

  // Defaults are not re-seeded on a company change, so user edits survive it.
  useEffect(() => {
    onRecordCompanyIdChange(watchedSelectedCompany?.id);
  }, [watchedSelectedCompany?.id, onRecordCompanyIdChange]);

  const previousCanPrefillSalespersonRef = useRef(canPrefillSalesperson);
  useEffect(() => {
    if (previousCanPrefillSalespersonRef.current === canPrefillSalesperson) return;
    previousCanPrefillSalespersonRef.current = canPrefillSalesperson;
    if (defaultProject || methods.getFieldState('salespeople').isDirty) return;

    const currentUser = userStore.user;
    const salespeople = methods.getValues('salespeople') ?? [];
    const holdsOnlyCurrentUser =
      salespeople.length === 1 && salespeople[0]?.id === currentUser.id;

    if (canPrefillSalesperson && salespeople.length === 0) {
      methods.setValue('salespeople', [currentUser], { shouldDirty: false });
    } else if (!canPrefillSalesperson && holdsOnlyCurrentUser) {
      methods.setValue('salespeople', [], { shouldDirty: false });
    }
  }, [canPrefillSalesperson, defaultProject, methods, userStore.user]);

  const itemizeMaterialRateDefaultOn = recordCompanySettings.isEnabled(
    'itemize_material_rate_default_on',
  );
  const previousItemizeDefaultOnRef = useRef(itemizeMaterialRateDefaultOn);
  useEffect(() => {
    if (previousItemizeDefaultOnRef.current === itemizeMaterialRateDefaultOn) return;
    previousItemizeDefaultOnRef.current = itemizeMaterialRateDefaultOn;
    if (defaultProject || methods.getFieldState('materialRateItemized').isDirty) return;

    methods.setValue('materialRateItemized', itemizeMaterialRateDefaultOn, {
      shouldDirty: false,
    });
  }, [itemizeMaterialRateDefaultOn, defaultProject, methods]);

  // The OrderDefaultsPanel reads `serviceClassOptions` from this context for its
  // autocomplete; without this fetch the list is empty whenever the setting turns
  // on or the company changes. Lives here so the panel grep gate against
  // panel-side useEffect/useFeatureFlag stays at 0.
  const { loadServiceClassOptions, serviceClassOptions } = useServiceClassOptions();
  useEffect(() => {
    const companyId = watchedSelectedCompany?.id;
    if (!companyId || !showServiceClass) return;
    void loadServiceClassOptions(companyId);
  }, [loadServiceClassOptions, watchedSelectedCompany?.id, showServiceClass]);

  // Subscribe to `account` so the rate preloads can refire with the new
  // accountId. Mirrors the legacy form (`watchedAccount = watch(
  // 'account')`). Same pattern as watchedSelectedCompany / watchedPhases —
  // panel-side useWatch is forbidden, so the Provider does it once.
  const watchedAccount = useWatch({
    control: methods.control,
    name: 'account',
  });

  const hasCustomerAccount = Boolean(watchedAccount?.id);
  useEffect(() => {
    onCustomerAccountChange?.(hasCustomerAccount);
  }, [hasCustomerAccount, onCustomerAccountChange]);

  // Cascading reset (LocationDetailsPanel): when the user
  // changes `company`, every field whose options are scoped to the previous
  // company becomes stale, so we wipe them before the next typeahead populates
  // fresh options. This mirrors the legacy form one-for-one —
  // the legacy effect cleared account, chargeToAccount, projectMaterialTotals,
  // materialRates, and site (plus the transient `tmpMaterial` input text, which
  // no longer exists in v2's MaterialsPanel). `shouldDirty: false` is critical —
  // the reset is internal Provider housekeeping, NOT a user-driven field edit,
  // so it MUST NOT pollute `formState.dirtyFields`. If we set shouldDirty: true
  // here, the sparse PATCH (deparseUpdateV2) would ship these fields
  // as cleared on every company change even when the user only tweaked unrelated
  // fields. The prevCompanyIdRef gate keeps the effect inert on initial mount
  // (prev === curr); the cascade only fires on a real transition.
  const prevCompanyIdRef = useRef<string | undefined>(watchedSelectedCompany?.id);
  useEffect(() => {
    const currentCompanyId = watchedSelectedCompany?.id;
    if (prevCompanyIdRef.current === currentCompanyId) return;
    prevCompanyIdRef.current = currentCompanyId;
    // Internal reset: `resetField` with `defaultValue` is the RHF primitive for
    // "treat this field as if the form had been initialized with this value" —
    // it rebases defaultValue + value AND clears dirty/touched in one pass.
    // Without it, a user who previously edited one of these fields and then
    // switched companies would carry a stale dirty marker through the cascade,
    // and deparseUpdateV2 would ship that field as cleared in the
    // PATCH on every company change. The dirty marker tracks "did the user edit
    // this since the form mounted", and the cascading reset is explicitly NOT a
    // user edit. The follow-up setValue with `shouldDirty: false` is the belt-
    // and-suspenders pass: it pins the stored value even if a future RHF
    // behavior leaves defaultValue untouched by resetField.
    //
    // The `as never` bridge is the bottom-type escape hatch: e.g.
    // ProjectFormSchemaInterface['account'] is inferred from `nameIdSchemaRequired`
    // as `{ id: string; name: string }`, which TS rejects when assigning `null`.
    // The grep guard against the broader escape hatches still passes because
    // `as never` is a bottom-type narrowing, not a widening assertion.
    const clearCascadeField = (
      name: keyof ProjectFormSchemaInterface,
      value: unknown,
    ) => {
      methods.resetField(name, { defaultValue: value as never, keepDirty: false });
      methods.setValue(name, value as never, {
        shouldDirty: false,
        shouldValidate: false,
      });
    };
    clearCascadeField('account', null);
    clearCascadeField('chargeToAccount', null);
    clearCascadeField('site', {});
    clearCascadeField('projectMaterialTotals', [EmptyProjectMaterialTotalItem]);
    clearCascadeField('materialRates', []);
    // The Pick Up / Drop Off / Staging site selectors are scoped to the
    // company too — their saved sites belong to the previous company, so the
    // form values are wiped here. The matching on-screen blanking lives in
    // LocationDetailsPanel (it owns the SiteSelection display state); the panel
    // resets that state on the same company transition.
    clearCascadeField('pickUpWayPoint', null);
    clearCascadeField('dropOffWayPoint', null);
    clearCascadeField('stagingWayPoint', null);
    clearCascadeField('taxRateSchedule', null);
  }, [watchedSelectedCompany?.id, methods]);

  // Account → department auto-seed. Mirrors the legacy form's
  // changeDepartmentDefault: when the user selects or changes the account,
  // fill an EMPTY department from that account's own department so the
  // dispatcher doesn't have to re-pick it. Semantics preserved from legacy:
  //  - Only fills when department is blank — never clobbers a manual choice
  //    (re-checked after the async fetch, since the user may pick one while the
  //    lookup is in flight).
  //  - The prevAccountIdRef gate fires the seed only on a genuine account
  //    transition, so it stays inert on initial mount and never spuriously
  //    dirties `department` on an existing project.
  //  - shouldDirty:true is load-bearing: v2's sparse PATCH (deparseUpdateV2,
  //    useProjects/models.ts department field) only ships `department_id` when
  //    the field is dirty, so the auto-filled value must travel with the
  //    account change. Effect lives here — never in a panel — to keep panels
  //    render-only.
  const prevAccountIdForDepartmentRef = useRef<string | undefined>(watchedAccount?.id);
  useEffect(() => {
    const accountId = watchedAccount?.id;
    if (prevAccountIdForDepartmentRef.current === accountId) return;
    prevAccountIdForDepartmentRef.current = accountId;
    if (!accountId) return;
    const departmentAlreadySet = Boolean(methods.getValues('department')?.id);
    if (departmentAlreadySet) return;

    const controller = new AbortController();
    const seedDepartmentFromAccount = async () => {
      try {
        // Shares the cache with CustomerAccountNotes so one account GET serves both.
        const account = await queryClient.fetchQuery(
          accountQueryOptions(
            methods.getValues('company')?.id ||
              defaultProject?.company?.id ||
              effectiveCompanyId,
            accountId,
          ),
        );
        const departmentChosenDuringFetch = Boolean(methods.getValues('department')?.id);
        if (
          controller.signal.aborted ||
          departmentChosenDuringFetch ||
          !account.department
        )
          return;
        methods.setValue(
          'department',
          { id: account.department.id, name: account.department.name },
          { shouldDirty: true, shouldValidate: false },
        );
      } catch {
        // A failed lookup just skips the auto-seed.
      }
    };
    void seedDepartmentFromAccount();

    return () => controller.abort();
    // Pin deps to the account id transition, mirroring the department-refresh /
    // cascading-reset effects above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedAccount?.id]);

  // Subscribe to `prevailingWage` so the FreightRatesPanel re-renders the
  // FreightRates component when the user toggles the checkbox inside its
  // bottomContent. Mirrors the legacy form's `prevailingWage={watch
  // ('prevailingWage')}` — the Provider owns the subscription so the panel
  // grep guard against panel-side `watch(`/`useWatch` stays at 0.
  const watchedPrevailingWage = useWatch({
    control: methods.control,
    name: 'prevailingWage',
  });

  // Subscribe to `taxRateSchedule` so the TaxPanel can read it via context
  // for the `disabled={Boolean(watchedTaxRateSchedule)}` state on `taxRate`
  // (mirrors the legacy form), and so the auto-populate effect
  // below can react to schedule selection. The Provider owns the subscription
  // so the panel grep gate against panel-side `useWatch`/`watch(` stays at 0.
  const watchedTaxRateSchedule = useWatch({
    control: methods.control,
    name: 'taxRateSchedule',
  });

  // Tax-rate auto-populate effect — mirrors the legacy form.
  // The AutocompleteTaxRateSchedulesTypeahead.parseFn extends the schema's
  // {label, value} shape with an `_entity: TaxRateScheduleTypeahead` field
  // that carries the schedule's `rate` (string | null). When the user picks
  // a schedule we mirror that rate into `taxRate` so the read-only number
  // field reflects the chosen schedule. The cast below narrows the schema's
  // mixed<{label, value}>() type to the typeahead's actual runtime shape; it
  // is a structural cast (not `as any`/`as unknown`) so the v2 grep gate is
  // satisfied. Legacy semantics preserved: when `_entity` is absent (e.g.
  // user cleared the schedule), the effect is a no-op — the previous
  // taxRate value persists so the user can still edit it manually.
  //
  // Effect deps pin to the primitive `schedule.value` (the schedule id) and
  // intentionally exclude the watched object + `methods`. RHF's useWatch
  // returns the field's current value, but the inner setValue('taxRate') call
  // triggers a Provider re-render and (depending on RHF internals) can
  // produce a new wrapper reference for the unchanged watched value — that
  // turns a reference-equality gate into an infinite render loop. The
  // primitive id mirrors the department-refresh / cascading-reset pattern above and makes
  // the effect fire ONLY when the user actually picks (or clears) a schedule.
  const watchedTaxRateScheduleValue = (
    watchedTaxRateSchedule as { value?: string } | null | undefined
  )?.value;
  useEffect(() => {
    const schedule = watchedTaxRateSchedule as
      | {
          _entity?: { rate?: string | number | null };
          label?: string;
          value?: string;
        }
      | null
      | undefined;
    const entity = schedule?._entity;
    if (!entity) return;
    const rate = entity.rate;
    // shouldDirty:true is load-bearing for the sparse PATCH. deparseUpdateV2
    // only emits fields present in dirtyFields, and `tax_rate`
    // comes solely from the `taxRate` key. The legacy form ran
    // the same effect WITHOUT shouldDirty, but its full PATCH shipped tax_rate
    // regardless; v2's sparse PATCH would silently drop it when the user picks
    // a schedule (taxRateSchedule goes dirty → tax_rate_schedule_id, but the
    // auto-populated taxRate stays clean → tax_rate omitted). Marking it dirty
    // here keeps tax_rate traveling with tax_rate_schedule_id. This only fires
    // on a genuine user selection — `_entity` is attached by the typeahead's
    // parseFn, NOT present on the schedule loaded into defaults at mount — so
    // it never spuriously dirties taxRate for an untouched form.
    methods.setValue(
      'taxRate',
      rate !== null && rate !== undefined ? Number(rate) : null,
      { shouldDirty: true },
    );
    // watchedTaxRateSchedule + methods read via closure; primitive id dep
    // keeps the effect inert across unrelated Provider re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedTaxRateScheduleValue]);

  const preloads = useProjectFormV2Preloads({
    companyId: watchedSelectedCompany?.id,
    accountId: watchedAccount?.id,
  });

  // Initial material-rates load → form rows. Mirrors the legacy form.
  // The dialog fetches rates for the project
  // (separate API from /projects) and hands them to the Provider; the
  // Provider waits for the projectMaterialTotals defaults to settle, then
  // maps each rate to its parent total via the shared helper and writes
  // them into the form. shouldDirty:false because this is initial data, NOT
  // a user edit — keeping it out of dirtyFields means the rates dispatch on
  // submit only sends rows the user actually touched.
  //
  // The flag applies the mapping once PER load: it's set after a successful
  // array payload and reset whenever `initialMaterialRates` goes back to a
  // non-array (the dialog sets it to `null` while fetching). This mirrors
  // legacy's `setHasAppliedInitialMaterialRates(false)` reset, and matters
  // because the Provider is not keyed by project: if it
  // is reused across a project switch, `initialMaterialRates` transitions
  // array → null → array, and without the reset the once-only guard would
  // lock the first project's rates in forever and the new project's fetched
  // rates would never reach form state. The within-load guard below still
  // prevents re-applying on a mere prop-identity change, so user edits made
  // after the load are never clobbered.
  const appliedInitialRatesRef = useRef(false);
  useEffect(() => {
    if (!Array.isArray(initialMaterialRates)) {
      appliedInitialRatesRef.current = false;
      return;
    }
    if (appliedInitialRatesRef.current) return;
    // Filter out null/undefined slots (yup `.notRequired()` widens entries
    // to nullable); the helper signature only handles defined rows.
    const materialTotals = (methods.getValues('projectMaterialTotals') ?? []).filter(
      (row): row is NonNullable<typeof row> => !!row,
    );
    const formRates = mapInitialMaterialRatesToFormRows(
      initialMaterialRates,
      materialTotals,
      Boolean(isCopyAndEdit),
    );
    methods.setValue(
      'materialRates',
      formRates as ProjectFormSchemaInterface['materialRates'],
      { shouldDirty: false, shouldValidate: false },
    );
    appliedInitialRatesRef.current = true;
    // methods is stable across renders; isCopyAndEdit only matters at
    // first-apply time so it doesn't drive re-application.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMaterialRates]);

  // Identity-stable handler so `<FreightRates onDirtyStatusChange={...}/>`
  // doesn't churn its internal effects whenever an unrelated piece of
  // context state updates. The body intentionally does nothing — RHF
  // already tracks dirtiness on its own, and the legacy form
  // forwards this signal into a setter that is now obsolete in v2.
  const onDirtyStatusChange = useCallback(() => {
    // no-op: v2 reads dirtiness from RHF formState directly.
  }, []);

  // Mirrors the legacy form. An un-saved project (no id yet) has no
  // add-on assignments, so it returns an empty list. The outer fn
  // closure binds the `rateOwnerType`; the inner thunk is what
  // `<FreightRates/>` actually invokes.
  const projectIdRef = useRef<Nullable<string> | undefined>(defaultProject?.id);
  useEffect(() => {
    projectIdRef.current = defaultProject?.id;
  }, [defaultProject?.id]);
  const addOnCompanyId = watchedSelectedCompany?.id || effectiveCompanyId;
  const fetchParentAddOnAssignments = useCallback(
    (rateOwnerType: RateOwnerType) =>
      async (signal?: AbortSignal): Promise<AddOnAssignment[]> => {
        const projectId = projectIdRef.current;
        if (!projectId) return [];

        const response =
          await getV1CompaniesCompanyIdProjectsProjectIdAutoAddOnAssignments({
            path: {
              'company-id': addOnCompanyId,
              'project-id': projectId,
            },
            query: {
              'filter[account_type]':
                rateOwnerType !== RateOwnerType.DRIVER ? rateOwnerType : undefined,
            },
            signal,
          });

        return response.data.data.map((addOnAssignment) =>
          AddOnAssignment.parse(addOnAssignment),
        );
      },
    [addOnCompanyId],
  );

  const callbacks = useMemo<ProjectFormV2Callbacks>(
    () => ({
      fetchCustomerRates: preloads.fetchCustomerRates,
      fetchVendorRates: preloads.fetchVendorRates,
      fetchDriverRates: preloads.fetchDriverRates,
      fetchParentAddOnAssignments,
      onDirtyStatusChange,
    }),
    [
      preloads.fetchCustomerRates,
      preloads.fetchVendorRates,
      preloads.fetchDriverRates,
      fetchParentAddOnAssignments,
      onDirtyStatusChange,
    ],
  );

  // Backed by state (not a ref) so that the Set's identity changes on every AI
  // edit. The Set is replaced immutably (never mutated in place), which lets the
  // context `value` memo update and reactive consumers re-rendering off
  // `ctx.aiEditedFields.has(...)` see fresh membership. A ref would mutate
  // silently and leave those consumers stale.
  const [aiEditedFields, setAiEditedFields] = useState<
    ReadonlySet<keyof ProjectFormSchemaInterface>
  >(() => new Set());

  const applyAIPatch = useCallback(
    (partial: Partial<ProjectFormSchemaInterface>) => {
      const keys = Object.keys(partial) as Array<keyof ProjectFormSchemaInterface>;
      keys.forEach((key) => {
        const value = partial[key];
        methods.setValue(key, value as ProjectFormSchemaInterface[typeof key], {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
      setAiEditedFields((prev) => {
        const next = new Set(prev);
        keys.forEach((key) => next.add(key));
        return next;
      });
    },
    [methods],
  );

  const getFieldMetadata = useCallback(
    <K extends keyof ProjectFormSchemaInterface>(
      key: K,
    ): ProjectFieldRuntimeMetadata<K> => {
      const fieldError = (
        methods.formState.errors as FieldErrors<ProjectFormSchemaInterface>
      )[key];
      return {
        ...projectFieldMetadata[key],
        currentValue: methods.getValues(key),
        isDirty: Boolean(methods.formState.dirtyFields[key]),
        isAIEdited: aiEditedFields.has(key),
        validationError:
          typeof fieldError?.message === 'string' ? fieldError.message : undefined,
      };
    },
    [methods, aiEditedFields],
  );

  const validateField = useCallback(
    <K extends keyof ProjectFormSchemaInterface>(key: K): Promise<boolean> =>
      methods.trigger(key),
    [methods],
  );

  // Dev/test-only `window.__projectFormV2__` hook. Exposes
  // applyAIPatch + getAIEditedFields so the deferred AI-patch e2e suite and
  // manual QA can drive the form's AI surface without rendering a helper UI.
  // Vite sets `import.meta.env.MODE` to 'development' in `vite dev`, 'test' in
  // vitest, and 'production' in `vite build`. The guard mirrors the plan doc:
  // ONLY skip in production — every other mode (dev, test) installs the hook.
  // Cleanup runs on unmount so a Provider remount produces a single live hook
  // bound to the latest applyAIPatch closure.
  useEffect(() => {
    if (import.meta.env.MODE === 'production') return;
    window.__projectFormV2__ = {
      applyAIPatch,
      getAIEditedFields: () => [...aiEditedFields],
    };
    return () => {
      delete window.__projectFormV2__;
    };
  }, [applyAIPatch, aiEditedFields]);

  // Inline-error mapping. ModelError_Item carries the BE field
  // shape (snake_case for most rails fields, but a few schema keys are
  // already snake_case — `contract_number`, `dispatch_number`). Try a direct
  // match first so those land without a conversion round-trip, then
  // snake → camel for the conventional cases. Anything that doesn't map to
  // a schema key falls through to the toast so the user still gets feedback
  // for cross-cutting errors (model: 'project', field: 'base', etc.).
  // (`toasterStore` is destructured from useStores() at the top of the Provider.)
  const schemaKeys = useMemo<ReadonlySet<keyof ProjectFormSchemaInterface>>(
    () =>
      new Set(
        Object.keys(projectFormSchema.fields) as Array<keyof ProjectFormSchemaInterface>,
      ),
    [],
  );
  useEffect(() => {
    if (!errors || errors.length === 0) return;
    for (const err of errors) {
      const direct = err.field;
      const camel = err.field.replace(/_([a-z0-9])/g, (_, ch: string) =>
        ch.toUpperCase(),
      );
      const directKey = direct as keyof ProjectFormSchemaInterface;
      const camelKey = camel as keyof ProjectFormSchemaInterface;
      const matched: keyof ProjectFormSchemaInterface | null = schemaKeys.has(directKey)
        ? directKey
        : schemaKeys.has(camelKey)
          ? camelKey
          : null;
      if (matched) {
        methods.setError(matched as Path<ProjectFormSchemaInterface>, {
          type: 'manual',
          message: err.message,
        });
      } else {
        toasterStore.push(alert(err.message, AlertTypes.error));
      }
    }
    // methods is stable; schemaKeys is referentially stable via the empty-dep
    // useMemo above. Re-run only when `errors` identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  // The FreightRatesPanel writes here via <FreightRates ref={…}>; the
  // shell reads it at submit time to extract the rule-based UI's site-rate
  // operations (+ optional add-on assignments). The ref's identity stays
  // stable across re-renders, so the value memo's deps don't need to list
  // it — useRef returns the same object until unmount.
  const freightRatesRef = useRef<FreightRatesHandler | null>(null);

  // Shared with the FileAttachmentsPanel (writes the ref) and the shell (reads it at
  // submit/close to flush queued attachments). Stable identity across renders,
  // so it's omitted from the value memo's deps — same as freightRatesRef.
  const fileAttachmentsRef = useRef<FileAttachmentsHandler | null>(null);

  const value = useMemo<ProjectFormV2ContextValue>(
    () => ({
      dispatchNumberLabel,
      flags,
      showDefaultDigitalTicketView,
      showContractNumber,
      showServiceClass,
      isDepartmentRequired,
      isSalespersonRequired,
      isFreightRateRequired,
      keepFreightRatesMounted,
      isRateByDepartmentNameRuleOn,
      isTaxRequired,
      serviceClassOptions,
      defaultProject,
      isCopyAndEdit: Boolean(isCopyAndEdit),
      sourceProjectId,
      watchedSelectedCompany,
      watchedPhases,
      watchedAccount,
      watchedPrevailingWage,
      watchedTaxRateSchedule,
      preloads,
      callbacks,
      applyAIPatch,
      aiEditedFields,
      getFieldMetadata,
      validateField,
      freightRatesRef,
      fileAttachmentsRef,
      onRateCardsLoadedChange,
    }),
    [
      dispatchNumberLabel,
      flags,
      showDefaultDigitalTicketView,
      showContractNumber,
      showServiceClass,
      isDepartmentRequired,
      isSalespersonRequired,
      isFreightRateRequired,
      keepFreightRatesMounted,
      isRateByDepartmentNameRuleOn,
      isTaxRequired,
      serviceClassOptions,
      defaultProject,
      isCopyAndEdit,
      sourceProjectId,
      watchedSelectedCompany,
      watchedPhases,
      watchedAccount,
      watchedPrevailingWage,
      watchedTaxRateSchedule,
      preloads,
      callbacks,
      applyAIPatch,
      aiEditedFields,
      getFieldMetadata,
      validateField,
      onRateCardsLoadedChange,
    ],
  );

  return (
    <FormProvider {...methods}>
      <ProjectFormV2Context.Provider value={value}>
        {children}
      </ProjectFormV2Context.Provider>
    </FormProvider>
  );
}
